/**
 * Schema.org helpers for structured data markup
 * Generates consistent, reusable schema across all pages
 */

export interface BreadcrumbItem {
    name: string;
    url: string;
    position: number;
}

/**
 * Generate BreadcrumbList schema.org markup
 * Used across all calculator and guide pages for SEO and breadcrumb navigation
 * @param items Array of breadcrumb items with name, url, and position
 */
export function generateBreadcrumbList(items: BreadcrumbItem[]) {
    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: items.map(item => ({
            '@type': 'ListItem',
            position: item.position,
            name: item.name,
            item: `https://www.azure-calc.co.uk${item.url}`,
        })),
    };
}

/**
 * Generate FAQPage schema.org markup
 * Used for pages with multiple FAQ sections
 * @param faqs Array of question-answer pairs
 */
export function generateFAQPage(
    faqs: Array<{
        question: string;
        answer: string;
    }>
) {
    return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map(faq => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer,
            },
        })),
    };
}

/**
 * Generate WebSite schema.org markup
 * Used once on App.tsx or a dedicated schema page for sitewide SEO
 * Includes organization info, search action, and URL structure
 */
export function generateWebsiteSchema() {
    return {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'AzureCalc.uk',
        url: 'https://www.azure-calc.co.uk',
        description:
            'Free independent Azure cost calculator for UK engineers. Estimate Blob Storage, Logic Apps, Functions, App Service, networking, and more. Official pricing updated nightly.',
        potentialAction: {
            '@type': 'SearchAction',
            target: {
                '@type': 'EntryPoint',
                urlTemplate: 'https://www.azure-calc.co.uk/search?q={search_term_string}',
            },
            query_input: 'required name=search_term_string',
        },
        publisher: {
            '@type': 'Organization',
            name: 'AzureCalc.uk',
            url: 'https://www.azure-calc.co.uk',
            logo: 'https://www.azure-calc.co.uk/logo.png',
            sameAs: [
                'https://twitter.com/azurecalc',
            ],
        },
    };
}

/**
 * Generate Organization schema.org markup
 * Used for consistent organization representation across pages
 */
export function generateOrganizationSchema() {
    return {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'AzureCalc.uk',
        url: 'https://www.azure-calc.co.uk',
        logo: 'https://www.azure-calc.co.uk/logo.png',
        description:
            'Independent Azure cost calculator providing real-time pricing from the Azure Retail Prices API for UK South region.',
        sameAs: [
            'https://twitter.com/azurecalc',
        ],
    };
}
